import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, get, hostsApi, patch, post, type Host, type InstallJob, type JoinToken, type JoinTokenCreated, type NodeEgress, type NodeInfo, type NodePatch, type NodeRole, type ProvisioningKeys } from '../api'
import { isDanger, useDefaultGroup, useDryRun } from './network/groupAdmin'
import { WarningNotes } from './network/Warnings'
import { Button, Copyable, Field, Notice, type Lamp } from '../components/ui'
import { Segmented } from '../live-ui/ui'
import { CenterKey, InstallForm, InstallProgress } from './Hosts'
import { HOSTS_KEY, PROV_KEYS_KEY, hostErrorText, installOffText, useHostEvents } from './provisioning'
import { getAsOf } from './network/api'
import { noteRelayAsOf } from './network/live'
import { RevokeNode } from './network/RevokeNode'

/*
 * Managed media nodes (docs/m2-edge-node.md §2, §7; docs/edge-node.md):
 * the list with live state, a join token for a new node, the policy,
 * rotating the secret and revoking. The join token lives only in the
 * dialog's own state: not in the query cache, not in logs, gone on close.
 *
 * Provisioning (M2.4, docs/m2-provisioning.md §11): "Add node" installs
 * over SSH from the center when the provisioner answers; each node's host
 * (Host.node_id) shows its agent, release and commands in the node's row.
 */

export const NODES_KEY = ['admin-nodes'] as const

/** GET /api/admin/nodes; its X-Server-Time feeds the clock offset, its as_of the freshness of relay pages. */
export const fetchNodes = async () => {
  const r = await getAsOf<NodeInfo[]>('/api/admin/nodes')
  noteRelayAsOf(r.asOf, r.at)
  return r.data
}

/** Install script of docs/edge-node.md. */
// install.sh, compose.yaml and Caddyfile come from this console
// (/downloads/edge/, checked against its manifest.json): the repository is
// private (docs/edge-node.md).
const INSTALL_PATH = '/downloads/edge/install.sh'

const STATE_ORDER = ['live', 'starting', 'reconnecting', 'failed', 'waiting', 'stopped']

/** "3 live · 1 reconnecting", known states first. */
export function outputsText(outputs: Record<string, number> | undefined): string {
  const e = Object.entries(outputs ?? {}).filter(([, n]) => n > 0)
  if (!e.length) return '—'
  e.sort(([a], [b]) => (STATE_ORDER.indexOf(a) + 1 || 99) - (STATE_ORDER.indexOf(b) + 1 || 99))
  return e.map(([s, n]) => `${n} ${s}`).join(' · ')
}

export const mbps = (bps?: number) => (bps === undefined ? '—' : bps >= 1e9 ? `${(bps / 1e9).toFixed(2)} Gbps` : `${(bps / 1e6).toFixed(1)} Mbps`)

/** Status as people read it: a drained online node is "out of rotation". */
export function nodeStatus(n: NodeInfo): { text: string; lamp: Lamp } {
  if (n.status === 'revoked') return { text: 'Revoked', lamp: 'error' }
  if (n.status === 'offline') return { text: 'Offline', lamp: 'dark' }
  if (!n.accept_ingest) return { text: 'Out of rotation', lamp: 'busy' }
  return { text: 'Online', lamp: 'live' }
}

/** The node has not confirmed the latest desired outputs. */
export const outOfSync = (n: NodeInfo) => n.status !== 'revoked' && (n.desired_rev ?? 0) !== (n.acked_rev ?? 0)

const ROLE_OPTS: { value: NodeRole; label: string }[] = [
  { value: 'edge', label: 'Edge' },
  { value: 'hub', label: 'Hub' },
]
const EGRESS_OPTS: { value: NodeEgress; label: string }[] = [
  { value: 'direct', label: 'Direct' },
  { value: 'via_center', label: 'Via center' },
]

const VIA_CENTER_NOTE =
  'Via center: the node gets no platform keys. Its streams reach platforms through a hub over the routing network, so the routing network must be on.'

/**
 * GET /api/admin/nodes/add-options: manual_install false (the box,
 * NODE_MANUAL_INSTALL=false) = nodes only over SSH, no join token, no
 * integrations node. A center without the endpoint (404) keeps the manual install.
 */
export function useManualInstall() {
  const q = useQuery({
    queryKey: ['admin-node-add-options'],
    queryFn: async () => {
      try {
        return (await get<{ manual_install?: boolean }>('/api/admin/nodes/add-options')).manual_install !== false
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return true
        throw e
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  })
  // unknown while loading or on an error: no manual tab (the box must never show it)
  return { manual: q.data === true, loading: q.isLoading }
}

/** Provisioning is there unless GET /api/admin/provisioning/keys says 404 (no PROVISIONER_URL). */
export function useProvisioning() {
  const keys = useQuery({
    queryKey: PROV_KEYS_KEY,
    queryFn: async () => {
      try {
        return await hostsApi.keys()
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
    retry: false,
    staleTime: 60_000,
  })
  const on = keys.data !== null && !keys.isLoading && !keys.isError
  const live = useHostEvents(on)
  // Hosts come with events; the list is polled only while the stream is down.
  const hosts = useQuery({ queryKey: HOSTS_KEY, queryFn: hostsApi.list, enabled: on, retry: false, refetchInterval: live ? false : 10_000 })
  return { keys, hosts, on, live }
}

export type Dialog = { kind: 'add' } | { kind: 'job'; job?: InstallJob; host: Host } | { kind: 'host'; host: Host } | null

/** A centered dialog closed by ✕, Escape or a click outside. */
export function Modal({ title, testId, onClose, children, wide }: { title: ReactNode; testId: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      // the code window sits above and handles its own Escape
      if (e.key === 'Escape' && !document.querySelector('[data-testid="totp-dialog"]')) onClose()
    }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-bg/80">
      <div className="flex min-h-full items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} data-testid={testId} className={`rs-corners w-full ${wide === false ? 'max-w-3xl' : 'max-w-4xl'} rounded-lg border border-line-2 bg-panel`}>
          <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 className="text-lg font-semibold">{title}</h2>
            <Button variant="quiet" aria-label="Close" data-testid={`${testId}-close`} onClick={onClose}>
              ✕
            </Button>
          </header>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  )
}

/** PATCH error text: 409 means the node was revoked in the meantime. */
function patchError(e: unknown, name: string): string {
  if (e instanceof ApiError && e.status === 409) return `${name} is revoked: its settings can't change any more. Add a new node instead.`
  if (e instanceof ApiError && e.code === 'confirm_required') return `The center says this change breaks the air of live streams on ${name}: make it on the node card (Overview › Roles and relay), where it shows what it takes and asks for the confirmation.`
  return (e as Error).message
}

function diff(n: NodeInfo, f: Required<NodePatch>): NodePatch {
  const p: NodePatch = {}
  if (f.name.trim() !== n.name) p.name = f.name.trim()
  if (f.region.trim() !== n.region) p.region = f.region.trim()
  if (f.role !== n.role) p.role = f.role
  if (f.egress !== n.egress) p.egress = f.egress
  if (f.accept_ingest !== n.accept_ingest) p.accept_ingest = f.accept_ingest
  return p
}

/** What a change does, in words, for the confirmation. */
function consequences(n: NodeInfo, p: NodePatch): string[] {
  const out: string[] = []
  if (p.accept_ingest === false) out.push(`${n.name} leaves the rotation: new streams go to other nodes, broadcasts running on it continue.`)
  if (p.accept_ingest === true) out.push(`${n.name} is offered to users for new streams again.`)
  if (p.region !== undefined) out.push(`Region ${n.region || '—'} → ${p.region || '—'}: users who picked ${n.region || 'that region'} may get new addresses.`)
  if (p.role !== undefined) out.push(`Role ${n.role} → ${p.role}.`)
  if (p.egress === 'via_center') out.push(VIA_CENTER_NOTE)
  if (p.egress === 'direct') out.push(`${n.name} publishes to platforms itself and gets their keys for streams that go live on it.`)
  if (p.name !== undefined) out.push(`Name ${n.name} → ${p.name}.`)
  return out
}

export function NodeManage({ n, onChanged, onClose }: { n: NodeInfo; onChanged: () => Promise<unknown>; onClose: () => void }) {
  const [f, setF] = useState<Required<NodePatch>>({ name: n.name, region: n.region, role: n.role, egress: n.egress, accept_ingest: n.accept_ingest })
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [info, setInfo] = useState('')
  const changes = diff(n, f)
  const changed = Object.keys(changes).length > 0
  // GROUP-UI-3 (NG-18 C): the center's warnings for this change (V2: the last node of the automatic address)
  const dg = useDefaultGroup()
  const dry = useDryRun(confirm && changed ? { method: 'PATCH', path: `/api/admin/nodes/${encodeURIComponent(n.id)}`, body: changes } : null, dg.supported)
  const set = <K extends keyof NodePatch>(k: K, v: Required<NodePatch>[K]) => {
    setF((x) => ({ ...x, [k]: v }))
    setConfirm(false)
    setInfo('')
  }

  const run = async (fn: () => Promise<unknown>, ok: string, close = false) => {
    setBusy(true)
    setErr('')
    setInfo('')
    try {
      await fn()
      await onChanged()
      setInfo(ok)
      setConfirm(false)
      if (close) onClose()
    } catch (e) {
      setErr(patchError(e, n.name))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-testid="node-manage">
      <div className="flex flex-col gap-3" data-testid="node-policy">
        <h3 className="font-semibold">Policy</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" name={`node-name-${n.id}`} value={f.name} maxLength={64} onChange={(e) => set('name', e.target.value)} />
          <Field label="Region" name={`node-region-${n.id}`} value={f.region} maxLength={32} placeholder="eu-central" onChange={(e) => set('region', e.target.value)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {n.roles?.length ? (
            // relay roles (R0) replace the edge / hub role: they are edited on the node card
            <div className="flex flex-col gap-1.5" data-testid="node-policy-roles">
              <span className="text-[13px] text-text-soft">Roles</span>
              <span className="text-xs text-muted">
                {n.roles.join(', ')} — change them on the node card, Overview › Roles and relay.
              </span>
            </div>
          ) : (
            <Segmented label="Role" options={ROLE_OPTS} value={f.role} onChange={(v) => set('role', v)} />
          )}
          {n.roles?.length ? (
            // egress of a relay node breaks the air when it leaves via_center: one place with the center's confirmation
            <div className="flex flex-col gap-1.5" data-testid="node-policy-egress">
              <span className="text-[13px] text-text-soft">Egress</span>
              <span className="text-xs text-muted">
                {n.egress === 'via_center' ? 'via center' : 'direct'} — change it on the node card, Overview › Roles and relay.
              </span>
            </div>
          ) : (
            <Segmented label="Egress" options={EGRESS_OPTS} value={f.egress} onChange={(v) => set('egress', v)} />
          )}
        </div>
        {f.egress === 'via_center' && (
          <p data-testid="node-via-center" className="rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-xs text-warn">▲ {VIA_CENTER_NOTE}</p>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="node-accept" checked={f.accept_ingest} onChange={(e) => set('accept_ingest', e.target.checked)} className="h-4 w-4 accent-[var(--live)]" />
          Accept new streams (in rotation)
        </label>
        {confirm && changed ? (
          <div role="alertdialog" aria-label={`Apply changes to ${n.name}?`} data-testid="node-policy-confirm" className="flex flex-col gap-2.5 rounded-md border border-warn/50 bg-warn/[.07] p-3">
            <p className="font-semibold text-warn">Apply changes to {n.name}?</p>
            <WarningNotes warnings={dry.warnings} ctx={{ name: n.name, group: n.failover?.group_name }} testId="node-policy-warnings" />
            <ul className="list-disc space-y-1 pl-5 text-[13px] text-text-soft">
              {consequences(n, changes).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <p className="text-xs text-muted">{n.status === 'online' ? 'The node gets the new policy at once.' : 'The node gets it when it connects.'}</p>
            <div className="flex gap-2">
              <Button variant="quiet" onClick={() => setConfirm(false)}>
                Cancel
              </Button>
              <Button variant={isDanger(dry.warnings) ? 'danger' : 'primary'} disabled={busy || dry.pending} data-testid="node-policy-apply" onClick={() => void run(() => patch(`/api/admin/nodes/${n.id}`, changes), 'Saved.')}>
                Apply
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="primary" disabled={!changed || busy || !f.name.trim()} data-testid="node-policy-save" onClick={() => setConfirm(true)}>
              Save…
            </Button>
            {changed && (
              <Button variant="quiet" onClick={() => setF({ name: n.name, region: n.region, role: n.role, egress: n.egress, accept_ingest: n.accept_ingest })}>
                Undo changes
              </Button>
            )}
          </div>
        )}
        <Notice>{err}</Notice>
        {info && <Notice kind="info">{info}</Notice>}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold">Secret</h3>
          <p className="text-xs text-muted">The node gets a new secret over its channel and saves it; the old one works until it confirms. Only for a connected node.</p>
          <div>
            <Button
              disabled={busy || n.status !== 'online'}
              data-testid={`node-rotate-${n.id}`}
              onClick={() => void run(() => post(`/api/admin/nodes/${n.id}/rotate-credential`), `New secret sent to ${n.name}.`)}
            >
              Rotate secret
            </Button>
          </div>
        </div>
        <RevokeNode n={n} onRevoked={async () => (await onChanged(), onClose())} />
      </div>
    </div>
  )
}

/** Shell-safe: DNS name and e-mail only, so the command can be pasted as is. */
const HOST_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i
const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i
const REGION_RE = /^[a-z0-9._-]{0,32}$/i

/** An integrations node (docs/integrations-nodes.md): one container, no ports, the token in a file only its user reads. */
export function integrationsCommand(o: { host: string; token: string }): string {
  return [
    `install -d -m 700 /opt/integrations && umask 077 && printf '%s\\n' '${o.token}' > /opt/integrations/join_token && chown 10003 /opt/integrations/join_token`,
    `docker run -d --name strevix-integrations --restart unless-stopped \\`,
    `  -e CONTROL_URL=wss://${o.host}/node/v1/integrations -e JOIN_TOKEN_FILE=/run/join_token \\`,
    `  -v /opt/integrations/join_token:/run/join_token:ro -v strevix_integrations:/var/lib/integrations \\`,
    `  ${INTEGRATIONS_IMAGE}`,
  ].join('\n')
}

const INTEGRATIONS_IMAGE = 'ghcr.io/cooladm1n/restream_core/integrations:main'

type NodeKind = 'media' | 'integrations'
const KIND_OPTS: { value: NodeKind; label: string }[] = [
  { value: 'media', label: 'Media' },
  { value: 'integrations', label: 'Integrations' },
]

export function installCommand(o: { host: string; token: string; hostname: string; email: string; region: string }): string {
  const flags = [`--control-url wss://${o.host}/node/v1/connect`, `--join-token ${o.token}`, `--hostname ${o.hostname}`]
  if (o.email) flags.push(`--acme-email ${o.email}`)
  if (o.region) flags.push(`--region-hint ${o.region}`)
  return `curl -fsSL https://${o.host}${INSTALL_PATH} |\n  sudo sh -s -- ${flags.join(' \\\n    ')}`
}

/**
 * "Add node": install over SSH from the center (provisioning), or the manual
 * install with a join token (M2.1). The SSH mode is the default when the
 * provisioner can install; the token mode stays available either way, except
 * where the center has it off (the box: only SSH, no integrations node).
 */
export function AddNodeDialog({ keys, keysError, loading, onClose }: { keys: ProvisioningKeys | null | undefined; keysError: unknown; loading: boolean; onClose: () => void }) {
  const canInstall = !!keys?.install?.enabled
  const opts = useManualInstall()
  const manualOK = opts.manual
  const [mode, setMode] = useState<'ssh' | 'manual' | null>(null)
  const [title, setTitle] = useState<string | null>(null)
  const [job, setJob] = useState<InstallJob | null>(null)
  const shown = manualOK ? (mode ?? (loading || canInstall ? 'ssh' : 'manual')) : 'ssh'
  const manualHint = manualOK && <p className="text-sm text-muted">The manual install with a token works as before.</p>
  const onTitle = useCallback((t: string | null) => setTitle(t), [])
  const close = useCallback(() => onClose(), [onClose])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !document.querySelector('[data-testid="totp-dialog"]') && close()
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [close])

  const heading = job ? `Installing ${job.params?.slug ?? 'the node'}` : (title ?? 'Add a node')
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-bg/80">
      <div className="flex min-h-full items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && !job && close()}>
        <div role="dialog" aria-modal="true" aria-labelledby="join-title" data-testid="join-dialog" data-mode={shown} className="rs-corners w-full max-w-3xl rounded-lg border border-line-2 bg-panel">
          <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 id="join-title" className="text-lg font-semibold">
              {heading}
            </h2>
            <Button variant="quiet" aria-label="Close" data-testid="join-close" onClick={close}>
              ✕
            </Button>
          </header>
          {job ? (
            <div className="space-y-4 p-4">
              <InstallProgress jobId={job.id} initial={job} />
              <div className="flex justify-end">
                <Button variant="primary" data-testid="install-close" onClick={close}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              {!title && manualOK && (
                <div role="radiogroup" aria-label="How to install" className="flex gap-1 border-b border-line px-4 pt-3">
                  {(
                    [
                      ['ssh', 'Install over SSH', 'The center connects to the server and installs everything'],
                      ['manual', 'Manual install (token)', 'You run a command on the server'],
                    ] as const
                  ).map(([m, label, sub]) => (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={shown === m}
                      data-testid={`add-mode-${m}`}
                      onClick={() => setMode(m)}
                      className={`-mb-px min-h-[44px] rounded-t-md border px-3 py-1.5 text-left text-sm ${shown === m ? 'border-line border-b-panel bg-panel text-text' : 'border-transparent text-muted hover:text-text-soft'}`}
                    >
                      <span className="block font-medium">{label}</span>
                      <span className="hidden text-xs text-muted sm:block">{sub}</span>
                    </button>
                  ))}
                </div>
              )}
              {shown === 'manual' ? (
                <ManualJoin onClose={close} onTitle={onTitle} />
              ) : (
                <div className="p-4">
                  {loading || opts.loading ? (
                    <p className="text-sm text-muted">Checking the provisioner…</p>
                  ) : keysError ? (
                    <div data-testid="prov-off" className="space-y-2">
                      <Notice>{hostErrorText(keysError)}</Notice>
                      {manualHint}
                    </div>
                  ) : !keys ? (
                    <div data-testid="prov-off" data-reason="not_configured" className="space-y-2">
                      <Notice kind="info">Automatic installs are not set up on this center (no provisioner, PROVISIONER_URL).{manualOK ? ' Use the manual install with a token.' : ''}</Notice>
                    </div>
                  ) : !canInstall ? (
                    <div className="space-y-4">
                      <div data-testid="prov-off" data-reason={/release/i.test(keys.install?.reason ?? '') ? 'no_release' : 'not_configured'}>
                        <Notice kind="info">{installOffText(keys.install)}</Notice>
                      </div>
                      {manualHint}
                      <CenterKey keys={keys} />
                    </div>
                  ) : (
                    <InstallForm keys={keys} onStarted={setJob} onCancel={close} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Manual install (M2.1): POST /api/admin/nodes/join-tokens, then the token and
 * the install command once. The token stays in this component's state only.
 */
function ManualJoin({ onClose, onTitle }: { onClose: () => void; onTitle: (t: string | null) => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ kind: 'media' as NodeKind, name: '', region: '', role: 'edge' as NodeRole, egress: 'direct' as NodeEgress, hostname: '', email: '' })
  const integ = f.kind === 'integrations'
  const [created, setCreated] = useState<{ token: string; join: JoinToken; command: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const first = useRef<HTMLDivElement>(null)

  const close = () => {
    setCreated(null) // the token leaves the state (and the DOM) with the dialog
    onClose()
  }
  useEffect(() => {
    first.current?.querySelector('input')?.focus()
  }, [])
  useEffect(() => onTitle(created ? `Install ${created.join.name}` : null), [created, onTitle])

  const errors = {
    name: f.name.trim() ? '' : 'Required',
    region: REGION_RE.test(f.region.trim()) ? '' : 'Letters, digits, dots and dashes, up to 32',
    hostname: integ || HOST_RE.test(f.hostname.trim()) ? '' : 'DNS name of the server, e.g. fra1.ingest.example.com',
    email: integ || !f.email.trim() || EMAIL_RE.test(f.email.trim()) ? '' : 'An e-mail address',
  }
  const valid = !Object.values(errors).some(Boolean)
  const [touched, setTouched] = useState(false)

  const create = async () => {
    setTouched(true)
    if (!valid) return
    setBusy(true)
    setErr('')
    try {
      const body: JoinToken = integ
        ? { name: f.name.trim(), region: f.region.trim(), roles: ['integrations'] }
        : { name: f.name.trim(), region: f.region.trim(), role: f.role, egress: f.egress }
      // Plain request, not useMutation: the answer must not land in a query or mutation cache.
      const r = await post<JoinTokenCreated>('/api/admin/nodes/join-tokens', body)
      setCreated({
        token: r.token,
        join: r.join_token,
        command: integ
          ? integrationsCommand({ host: location.host, token: r.token })
          : installCommand({ host: location.host, token: r.token, hostname: f.hostname.trim(), email: f.email.trim(), region: f.region.trim() }),
      })
      void qc.invalidateQueries({ queryKey: NODES_KEY })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const field = (k: keyof typeof f, label: string, extra: { placeholder?: string; hint?: ReactNode; type?: string } = {}) => (
    <Field
      label={label}
      name={`join-${k}`}
      value={f[k]}
      autoComplete="off"
      error={touched ? errors[k as keyof typeof errors] : undefined}
      onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
      {...extra}
    />
  )

  return created ? (
    <div className="space-y-4 p-4" data-testid="join-result">
      <Notice kind="info">
        <b className="text-text">Valid for 1 hour and shown only once.</b> Copy it now: after you close this window it can't be shown again.
        {created.join.expires_at && <> Expires {new Date(created.join.expires_at).toLocaleTimeString()}.</>}
      </Notice>
      <div className="space-y-1">
        <p className="text-sm font-medium">Join token</p>
        <div data-testid="join-token">
          <Copyable label="Join token" value={created.token} />
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">{created.join.roles?.includes('integrations') ? 'Run on any server with Docker (as root)' : 'Run on the server (Ubuntu 22.04 / 24.04, as root)'}</p>
        <pre data-testid="join-command" className="overflow-x-auto rounded-md border border-line bg-bg p-3 font-mono text-xs whitespace-pre text-text">
          {created.command}
        </pre>
        <CopyButton value={created.command} label="Copy command" />
      </div>
      {created.join.roles?.includes('integrations') ? (
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted">
          <li>No ports to open: the node only connects out, to this center and to the platforms.</li>
          <li>It shows up here as online once it joins and takes the Twitch chat and the platform stats from the center.</li>
        </ul>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted">
          <li>The DNS name must point at the server and port 80 must be open, for the certificate.</li>
          <li>Ports 443, 1935 and 1936 must be free. Allow SSH in ufw before you enable it.</li>
          <li>
            The node shows up here as online once it joins. Region {created.join.region || '—'}, role {created.join.role}, egress{' '}
            {created.join.egress === 'via_center' ? 'via center' : 'direct'}.
          </li>
        </ul>
      )}
      <div className="flex justify-end">
        <Button variant="primary" data-testid="join-done" onClick={close}>
          Done
        </Button>
      </div>
    </div>
  ) : (
    <form
      className="space-y-4 p-4"
      onSubmit={(e) => {
        e.preventDefault()
        void create()
      }}
    >
      <Segmented label="Node type" options={KIND_OPTS} value={f.kind} onChange={(v) => setF((x) => ({ ...x, kind: v }))} />
      <div ref={first} className="grid gap-3 sm:grid-cols-2">
        {field('name', 'Name', { placeholder: 'fra1' })}
        {field('region', 'Region', { placeholder: 'eu-central', hint: 'Users pick it as their ingest region' })}
      </div>
      {integ ? (
        <p data-testid="join-integrations" className="text-xs text-muted">
          An integrations node reads platform APIs for the center: the Twitch chat and the live stats. It carries no video and opens no ports.
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Segmented label="Role" options={ROLE_OPTS} value={f.role} onChange={(v) => setF((x) => ({ ...x, role: v }))} />
            <Segmented label="Egress" options={EGRESS_OPTS} value={f.egress} onChange={(v) => setF((x) => ({ ...x, egress: v }))} />
          </div>
          {f.egress === 'via_center' && <p data-testid="join-via-center" className="rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-xs text-warn">▲ {VIA_CENTER_NOTE}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {field('hostname', 'Server DNS name', { placeholder: 'fra1.ingest.example.com', hint: 'For the install command and the certificate' })}
            {field('email', "Let's Encrypt e-mail", { placeholder: 'ops@example.com', hint: 'Optional', type: 'email' })}
          </div>
        </>
      )}
      <Notice>{err}</Notice>
      <div className="flex justify-end gap-2">
        <Button variant="quiet" type="button" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy} data-testid="join-create">
          Create token
        </Button>
      </div>
    </form>
  )
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      data-testid="join-copy-command"
      onClick={async () => {
        await navigator.clipboard?.writeText(value).catch(() => undefined)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  )
}
